'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

import { cn } from '@/lib/utils';

/** A tick mark: a plain value, or a value with an optional label rendered below it. */
export type SliderMark = number | { value: number; label?: React.ReactNode };

export type SliderProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  /**
   * Optional tick marks along the track. A number renders a bare tick at that
   * value; `{ value, label }` also renders a label beneath it (a labelled tick is
   * drawn taller). Horizontal orientation only.
   */
  marks?: SliderMark[];
};

/**
 * Slider with one thumb per value: a single value renders one handle, a
 * `[min, max]` pair renders a range, and N values render N handles (e.g. multiple
 * thresholds). Supports horizontal (default) and vertical orientation, and opt-in
 * tick `marks`. Styled with the Medintelekt tokens — accent track, primary
 * range/fill, primary-bordered thumb.
 */
const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  ({ className, value, defaultValue, min = 0, max = 100, orientation, marks, ...props }, ref) => {
    // One thumb per value; fall back to a single handle when uncontrolled with no default.
    const thumbCount = React.useMemo(() => {
      if (Array.isArray(value)) return value.length;
      if (Array.isArray(defaultValue)) return defaultValue.length;
      return 1;
    }, [value, defaultValue]);

    const normalizedMarks = React.useMemo(
      () => (marks ?? []).map((m) => (typeof m === 'number' ? { value: m } : m)),
      [marks],
    );
    // Ticks are positioned along the horizontal axis; not supported vertically.
    const showMarks = normalizedMarks.length > 0 && orientation !== 'vertical';
    const percent = (v: number): number =>
      max === min ? 0 : Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));

    return (
      <SliderPrimitive.Root
        ref={ref}
        min={min}
        max={max}
        value={value}
        defaultValue={defaultValue}
        orientation={orientation}
        className={cn(
          'relative flex touch-none select-none items-center data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-[11rem] data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
          showMarks && 'mb-9',
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track className="relative grow overflow-hidden rounded-full bg-accent data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5">
          <SliderPrimitive.Range className="absolute bg-primary data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full" />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }, (_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            className="block size-4 rounded-full border-[1.5px] border-primary bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          />
        ))}
        {showMarks && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-full mt-2">
            {normalizedMarks.map((mark, index) => (
              <div
                key={index}
                data-slider-mark
                className="absolute flex -translate-x-1/2 flex-col items-center gap-1.5"
                style={{ left: `${percent(mark.value)}%` }}
              >
                <span
                  data-slider-tick
                  className={cn(
                    'w-[1.5px] rounded-full',
                    mark.label != null ? 'h-[9px] bg-muted-foreground/60' : 'h-1.5 bg-border',
                  )}
                />
                {mark.label != null && (
                  <span
                    data-slider-mark-label
                    className="text-[11px] tabular-nums text-muted-foreground"
                  >
                    {mark.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
